import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { FiShare2, FiArrowLeft, FiArrowRight, FiGrid, FiList, FiInfo, FiTrash2, FiCheckSquare } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import ConfirmDangerModal from '../../layout/ReusableComponent/ConfirmDangerModal';
import { MobileMediaViewerOverlay } from '../../layout/ReusableComponent/MobileMediaViewer';
import StorageBoxBreadcrumbTrail from '../components/StorageBoxBreadcrumbTrail';
import {
  buildStorageBoxMediaViewerItems,
  indexOfMediaItem,
  isStorageBoxViewableMedia,
} from '../utils/mediaViewerItems';
import { APP_NAME } from '../../config/branding';
import {
  STORAGE_BOX_ROOT_LABEL,
  STORAGE_BOX_BIN_LABEL,
  STORAGE_BOX_FAVORITES_LABEL,
  STORAGE_BOX_LOCK_LABEL,
  STORAGE_BOX_BIN_RETENTION_DAYS,
  storageBoxHomeTitle,
} from '../constants';
import { colorTagDisplayName } from '../utils/itemMetaDisplay';
import StorageBoxItemMetaBadge from '../components/StorageBoxItemMetaBadge';
import { storageBoxApi, formatBytes } from '../api/storageBoxApi';
import { fetchAndDecryptLockFile, revokeObjectUrl } from '../utils/lockFileAccess';
import { collectFilesFromDrop } from '../utils/collectFilesFromDrop';
import { allowExternalFileDrop, isExternalFileDrag } from '../utils/fileDragDrop';
import PreviewPanel from '../components/PreviewPanel';
import { isStorageBoxAudioItem } from '../components/StorageBoxAudioPlayerModal';
import { useGlobalStorageBoxAudio } from '../../layout/ReusableComponent/audioPlayer/globalStorageBoxAudioSession';
import StorageBoxTextEditorModal, {
  isStorageBoxTextEditorItem,
} from '../components/StorageBoxTextEditorModal';
import DriveItemThumbnail from '../components/DriveItemThumbnail';
import StorageBoxItemActionsMenu from '../components/StorageBoxItemActionsMenu';
import StorageBoxItemGrid from '../components/StorageBoxItemGrid';
import StorageBoxNewMenu from '../components/StorageBoxNewMenu';
import StorageBoxSearchField from '../components/StorageBoxSearchField';
import StorageBoxToast from '../components/StorageBoxToast';
import StorageBoxSortSelect from '../components/StorageBoxSortSelect';
import { sortStorageBoxItems } from '../utils/sortStorageBoxItems';
import {
  buildSharedLockBreadcrumbTrail,
  isSharedLockOwnerCrumbId,
  sharedLockPageTitle,
} from '../utils/sharedLockBreadcrumbs';
import StorageBoxMoveCopyModal from '../components/StorageBoxMoveCopyModal';
import StorageBoxBulkSelectBar from '../components/StorageBoxBulkSelectBar';
import StorageBoxDestinationPickerModal from '../components/StorageBoxDestinationPickerModal';
import { isStorageBoxBulkSelectable, selectableItems } from '../utils/storageBoxBulkSelect';
import { useStorageBox } from '../StorageBoxContext';
import { StorageBoxWorkspaceSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import '../StorageBoxPage.css';

function DraggableRow({ item, children, disabled, isSelected }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.itemId,
    data: { item },
    disabled,
  });
  const rowClass = [
    'sb-table__row',
    isDragging ? 'sb-table__row--dragging' : '',
    isSelected ? 'sb-table__row--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <tr ref={setNodeRef} className={rowClass} {...listeners} {...attributes}>
      {children}
    </tr>
  );
}

function FolderRow({
  item,
  children,
  disabled,
  isSelected,
  fileDropActive,
  onFolderFileDragOver,
  onFolderFileDragLeave,
  onFolderFileDrop,
}) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: item.itemId,
    data: { item },
    disabled,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `folder-drop:${item.itemId}` });
  const setRefs = (node) => {
    setDragRef(node);
    setDropRef(node);
  };
  return (
    <tr
      ref={setRefs}
      className={[
        'sb-table__row',
        isDragging ? 'sb-table__row--dragging' : '',
        isOver || fileDropActive ? 'sb-table__row--drop-target' : '',
        isSelected ? 'sb-table__row--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onDragOver={(e) => onFolderFileDragOver?.(e, item.itemId)}
      onDragLeave={(e) => onFolderFileDragLeave?.(e, item.itemId)}
      onDrop={(e) => onFolderFileDrop?.(e, item.itemId)}
      {...listeners}
      {...attributes}
    >
      {children}
    </tr>
  );
}

export default function StorageBoxWorkspace() {
  const {
    readOnly,
    publicToken,
    parentId,
    items,
    breadcrumbs,
    selected,
    setSelected,
    loading,
    loadError,
    retryLoad,
    notice,
    writable,
    navIndex,
    navStackLength,
    navigateTo,
    goBack,
    goForward,
    handleUpload,
    openDeleteModal,
    openShareModal,
    loadFolder,
    refreshTree,
    refreshSummary,
    notify,
    openRenameModal,
    goToRoot,
    searchQuery,
    searchResults,
    searchLoading,
    searchActive,
    clearSearch,
    openItem,
    browseMode,
    activeLabel,
    activeColorTag,
    emptyBin,
    reloadCurrentView,
    viewMode,
    setViewMode,
    sortField,
    sortDir,
    sortPreferencesLoaded,
    lockSessionToken,
    inLockVault,
    getLockMasterKeyBytes,
    lockMasterKeyReady,
    vaultScope,
    lockRootItemId,
    activeSharedShare,
  } = useStorageBox();
  const globalStorageAudio = useGlobalStorageBoxAudio();
  const { isActive: storageAudioActive, openFile: openGlobalStorageAudio } = globalStorageAudio;
  const storageAudioWasActiveRef = useRef(false);

  const inBin = browseMode === 'bin';
  const inLock = browseMode === 'lock' || browseMode === 'shared';
  const inSharedLockView = browseMode === 'shared' && Boolean(activeSharedShare);
  const inFavorites = browseMode === 'favorites';
  const inLabel = browseMode === 'label';
  const inColorTag = browseMode === 'colorTag';
  const inSpecialView = inBin || inFavorites || inLabel || inColorTag;
  const bulkSelectEnabled = !readOnly && writable && !searchActive && !inSpecialView;

  const [draggingItem, setDraggingItem] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [folderFileDropId, setFolderFileDropId] = useState(null);
  const [dragActionModal, setDragActionModal] = useState(null);
  const [dragActionBusy, setDragActionBusy] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [textEditorOpen, setTextEditorOpen] = useState(false);
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [emptyBinModalOpen, setEmptyBinModalOpen] = useState(false);
  const [emptyBinBusy, setEmptyBinBusy] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkCheckedIds, setBulkCheckedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDestModal, setBulkDestModal] = useState(null);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const displayItems = useMemo(() => {
    const raw = searchActive ? searchResults : items;
    if (!sortPreferencesLoaded) return raw ?? [];
    return sortStorageBoxItems(raw, sortField, sortDir);
  }, [searchActive, searchResults, items, sortField, sortDir, sortPreferencesLoaded]);
  const isLoading = searchActive ? searchLoading : loading;

  const bulkSelectOptions = useMemo(
    () => ({ readOnly, writable, inLockVault }),
    [readOnly, writable, inLockVault]
  );

  const bulkSelectableList = useMemo(
    () => selectableItems(displayItems, bulkSelectOptions),
    [displayItems, bulkSelectOptions]
  );

  const bulkSelectedItems = useMemo(
    () => bulkSelectableList.filter((item) => bulkCheckedIds.has(item.itemId)),
    [bulkSelectableList, bulkCheckedIds]
  );

  const bulkSelectedFolderIds = useMemo(() => {
    const ids = new Set();
    bulkSelectedItems.forEach((item) => {
      if (item.type === 'folder') ids.add(item.itemId);
    });
    return ids;
  }, [bulkSelectedItems]);

  useEffect(() => {
    setBulkSelectMode(false);
    setBulkCheckedIds(new Set());
  }, [parentId, browseMode, searchActive]);

  const exitBulkSelectMode = useCallback(() => {
    setBulkSelectMode(false);
    setBulkCheckedIds(new Set());
  }, []);

  const toggleBulkCheck = useCallback((itemId) => {
    setBulkCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const selectAllBulk = useCallback(() => {
    setBulkCheckedIds(new Set(bulkSelectableList.map((item) => item.itemId)));
  }, [bulkSelectableList]);

  const selectNoneBulk = useCallback(() => {
    setBulkCheckedIds(new Set());
  }, []);

  const loadDestinationFolders = useCallback(
    async (folderId) => {
      if (inLockVault && lockSessionToken) {
        const parent = folderId ?? lockRootItemId;
        if (!parent) return [];
        const data = await storageBoxApi.lockListItems(parent, lockSessionToken);
        return (data.items || []).filter((row) => row.type === 'folder');
      }
      const data = await storageBoxApi.listTreeChildren(folderId ?? null);
      return (data.items || []).filter((row) => row.type === 'folder');
    },
    [inLockVault, lockSessionToken, lockRootItemId]
  );

  const runBulkDelete = useCallback(async () => {
    if (!bulkSelectedItems.length || bulkActionBusy) return;
    setBulkActionBusy(true);
    try {
      for (const item of bulkSelectedItems) {
        await storageBoxApi.deleteItem(item.itemId);
      }
      if (bulkSelectedItems.some((item) => item.itemId === selected?.itemId)) {
        setSelected(null);
      }
      refreshSummary();
      refreshTree();
      await reloadCurrentView();
      exitBulkSelectMode();
      setBulkDeleteOpen(false);
      notify(
        bulkSelectedItems.length === 1
          ? 'Moved to Bin'
          : `${bulkSelectedItems.length} items moved to Bin`
      );
    } catch (e) {
      notify(e.message || 'Delete failed');
    } finally {
      setBulkActionBusy(false);
    }
  }, [
    bulkSelectedItems,
    bulkActionBusy,
    selected,
    setSelected,
    refreshSummary,
    refreshTree,
    reloadCurrentView,
    exitBulkSelectMode,
    notify,
  ]);

  const runBulkTransfer = useCallback(
    async (mode, targetFolderId) => {
      if (!bulkSelectedItems.length || bulkActionBusy) return;
      setBulkActionBusy(true);
      try {
        const destId = targetFolderId ?? null;
        for (const item of bulkSelectedItems) {
          if (mode === 'move') {
            await storageBoxApi.moveItem(item.itemId, destId);
          } else {
            await storageBoxApi.copyItem(item.itemId, destId);
          }
        }
        if (mode === 'move' && bulkSelectedItems.some((item) => item.itemId === selected?.itemId)) {
          setSelected(null);
        }
        refreshTree();
        await reloadCurrentView();
        exitBulkSelectMode();
        setBulkDestModal(null);
        notify(
          mode === 'move'
            ? bulkSelectedItems.length === 1
              ? 'Moved'
              : `${bulkSelectedItems.length} items moved`
            : bulkSelectedItems.length === 1
              ? 'Copied'
              : `${bulkSelectedItems.length} items copied`
        );
      } catch (e) {
        notify(e.message || (mode === 'move' ? 'Move failed' : 'Copy failed'));
      } finally {
        setBulkActionBusy(false);
      }
    },
    [
      bulkSelectedItems,
      bulkActionBusy,
      selected,
      setSelected,
      refreshTree,
      reloadCurrentView,
      exitBulkSelectMode,
      notify,
    ]
  );

  const destinationRootId = inLockVault ? lockRootItemId : null;
  const destinationRootLabel = inLockVault ? STORAGE_BOX_LOCK_LABEL : STORAGE_BOX_ROOT_LABEL;

  const [lockMediaUrls, setLockMediaUrls] = useState({});
  const lockMediaErrorsRef = useRef({});
  const mediaViewerDismissedRef = useRef(false);
  const audioModalDismissedRef = useRef(false);
  const textEditorDismissedRef = useRef(false);
  const selectedMediaReadyRef = useRef(false);
  const mediaViewerOpenRef = useRef(false);
  const selectedIdRef = useRef(null);

  useEffect(() => {
    mediaViewerOpenRef.current = mediaViewerOpen;
  }, [mediaViewerOpen]);

  useEffect(() => {
    selectedIdRef.current = selected?.itemId || null;
    mediaViewerDismissedRef.current = false;
    audioModalDismissedRef.current = false;
    textEditorDismissedRef.current = false;
    selectedMediaReadyRef.current = false;
  }, [selected?.itemId]);

  useEffect(() => {
    if (!inLockVault || !lockSessionToken) {
      lockMediaErrorsRef.current = {};
      setLockMediaUrls((prev) => {
        Object.values(prev).forEach((u) => revokeObjectUrl(u));
        return {};
      });
    }
  }, [inLockVault, lockSessionToken]);

  useEffect(
    () => () => {
      Object.values(lockMediaUrls).forEach((u) => revokeObjectUrl(u));
    },
    [lockMediaUrls]
  );

  const flushLockViewerDecryptedMedia = useCallback(() => {
    setLockMediaUrls((prev) => {
      Object.values(prev).forEach((u) => revokeObjectUrl(u));
      return {};
    });
  }, []);

  const handleLockVideoError = useCallback(
    (e) => {
      notify(e?.message || 'Could not play this video');
      mediaViewerDismissedRef.current = true;
      setMediaViewerOpen(false);
      flushLockViewerDecryptedMedia();
    },
    [notify, flushLockViewerDecryptedMedia]
  );

  const lockPlayback = useMemo(() => {
    if (!inLockVault || !lockSessionToken) return null;
    return { lockSessionToken, urls: lockMediaUrls };
  }, [inLockVault, lockSessionToken, lockMediaUrls]);

  const mediaViewerItems = useMemo(
    () => buildStorageBoxMediaViewerItems(displayItems, publicToken, lockPlayback),
    [displayItems, publicToken, lockPlayback]
  );

  useEffect(() => {
    if (!mediaViewerOpen || !inLockVault || !lockSessionToken) return undefined;
    const masterKey = getLockMasterKeyBytes();
    if (!masterKey) return undefined;
    const entry = mediaViewerItems[mediaViewerIndex];
    if (!entry?.lockSource?.item || entry.type === 'video') return undefined;
    if (lockMediaUrls[entry.itemId]) return undefined;
    let cancelled = false;
    const { item } = entry.lockSource;
    fetchAndDecryptLockFile(item, lockSessionToken, masterKey)
      .then(({ blob }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setLockMediaUrls((prev) => ({ ...prev, [item.itemId]: url }));
      })
      .catch((e) => {
        if (cancelled) return;
        const message = e.message || 'Could not open this file';
        lockMediaErrorsRef.current[item.itemId] = message;
        notify(message);
        setMediaViewerOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    mediaViewerOpen,
    mediaViewerIndex,
    mediaViewerItems,
    inLockVault,
    lockSessionToken,
    getLockMasterKeyBytes,
    lockMasterKeyReady,
    lockMediaUrls,
    notify,
  ]);

  const openAudioPlayer = useCallback(
    (fileItem) => {
      if (!isStorageBoxAudioItem(fileItem)) return false;
      audioModalDismissedRef.current = false;
      openGlobalStorageAudio(fileItem, { publicToken });
      return true;
    },
    [openGlobalStorageAudio, publicToken]
  );

  const queueAudioPlayNext = useCallback(
    (fileItem) => {
      if (!isStorageBoxAudioItem(fileItem)) return false;
      if (inBin) return false;
      audioModalDismissedRef.current = false;
      globalStorageAudio.queueFileNext(fileItem);
      return true;
    },
    [globalStorageAudio.queueFileNext, inBin]
  );

  const openTextEditor = useCallback((fileItem) => {
    if (inBin || !isStorageBoxTextEditorItem(fileItem)) return false;
    textEditorDismissedRef.current = false;
    setTextEditorOpen(true);
    return true;
  }, [inBin]);

  const openMediaViewer = useCallback(
    (fileItem) => {
      if (!isStorageBoxViewableMedia(fileItem)) return false;
      const decryptError = fileItem.isEncrypted ? lockMediaErrorsRef.current[fileItem.itemId] : '';
      if (decryptError) {
        notify(decryptError);
        return false;
      }
      mediaViewerDismissedRef.current = false;
      const idx = mediaViewerItems.findIndex((entry) => entry.itemId === fileItem.itemId);
      if (idx >= 0) {
        selectedMediaReadyRef.current = true;
        setMediaViewerIndex(idx);
      }
      setMediaViewerOpen(true);
      return true;
    },
    [mediaViewerItems, notify]
  );

  useEffect(() => {
    if (!mediaViewerOpen || mediaViewerDismissedRef.current || !selected?.itemId) return;
    const idx = mediaViewerItems.findIndex((entry) => entry.itemId === selected.itemId);
    if (idx < 0) {
      selectedMediaReadyRef.current = false;
      return;
    }
    if (!selectedMediaReadyRef.current) {
      selectedMediaReadyRef.current = true;
      setMediaViewerIndex(idx);
    }
  }, [mediaViewerOpen, mediaViewerItems, selected?.itemId]);

  const selectItem = useCallback(
    (item) => {
      if (
        item?.itemId &&
        item.itemId === selected?.itemId &&
        !inBin &&
        isStorageBoxTextEditorItem(item)
      ) {
        openTextEditor(item);
        return;
      }
      if (
        item?.itemId &&
        item.itemId === selected?.itemId &&
        !inBin &&
        isStorageBoxAudioItem(item)
      ) {
        openAudioPlayer(item);
        return;
      }
      if (
        item?.itemId &&
        item.itemId === selected?.itemId &&
        !inBin &&
        isStorageBoxViewableMedia(item)
      ) {
        openMediaViewer(item);
        return;
      }
      setSelected(item);
    },
    [selected?.itemId, inBin, setSelected, openMediaViewer, openAudioPlayer, openTextEditor]
  );

  useEffect(() => {
    if (inBin || !selected?.itemId) {
      setTextEditorOpen(false);
      return;
    }
    if (!isStorageBoxTextEditorItem(selected)) {
      setTextEditorOpen(false);
      return;
    }
    if (!textEditorDismissedRef.current) {
      setTextEditorOpen(true);
    }
  }, [selected?.itemId, inBin, selected]);

  useEffect(() => {
    if (storageAudioWasActiveRef.current && !storageAudioActive && selected?.itemId) {
      audioModalDismissedRef.current = true;
    }
    storageAudioWasActiveRef.current = storageAudioActive;
  }, [storageAudioActive, selected?.itemId]);

  useEffect(() => {
    if (inBin) return;
    if (audioModalDismissedRef.current) return;
    if (!selected?.itemId || !isStorageBoxAudioItem(selected)) return;
    openGlobalStorageAudio(selected, { publicToken });
  }, [selected?.itemId, inBin, selected, publicToken, openGlobalStorageAudio]);

  useEffect(() => {
    if (inBin || !selected || !isStorageBoxViewableMedia(selected)) {
      setMediaViewerOpen(false);
      return;
    }
    setMediaViewerIndex(indexOfMediaItem(mediaViewerItems, selected.itemId));
    setMediaViewerOpen(true);
    // mediaViewerItems / selected read when selection changes only (avoid reopen after list refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [selected?.itemId, inBin]);

  const handleOpenFile = useCallback(
    (fileItem) => {
      if (inBin) {
        setSelected(fileItem);
        return;
      }
      if (fileItem.type === 'folder') {
        openItem(fileItem);
        return;
      }
      setSelected(fileItem);
      if (isStorageBoxTextEditorItem(fileItem)) {
        openTextEditor(fileItem);
      } else if (isStorageBoxAudioItem(fileItem)) {
        openAudioPlayer(fileItem);
      } else if (isStorageBoxViewableMedia(fileItem)) {
        openMediaViewer(fileItem);
      } else {
        openItem(fileItem);
      }
    },
    [inBin, openItem, openMediaViewer, openAudioPlayer, openTextEditor, setSelected]
  );

  const uploadFromDataTransfer = async (dataTransfer, targetFolderId) => {
    if (readOnly || !writable || searchActive) return;
    const { files, relativePaths } = await collectFilesFromDrop(dataTransfer);
    const validFiles = files.filter((f) => f instanceof File);
    if (!validFiles.length) {
      notify('Could not read dropped files. Try Upload from New, or drop onto the folder background.');
      return;
    }
    const paths =
      validFiles.length === relativePaths.length
        ? relativePaths
        : validFiles.map((f) => f.name);
    const hasTree = paths.some((p) => p.includes('/'));
    await handleUpload(validFiles, hasTree ? paths : null, targetFolderId);
  };

  const onDrop = async (e) => {
    if (!isExternalFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    setDropActive(false);
    setFolderFileDropId(null);
    await uploadFromDataTransfer(e.dataTransfer, parentId);
  };

  const onFolderFileDragOver = (e, folderId) => {
    if (readOnly || !writable || searchActive) return;
    if (!allowExternalFileDrop(e)) return;
    setFolderFileDropId(folderId);
    setDropActive(false);
  };

  const onFolderFileDragLeave = (e, folderId) => {
    if (folderFileDropId !== folderId) return;
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setFolderFileDropId(null);
  };

  const onFolderFileDrop = async (e, folderId) => {
    if (!isExternalFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    setFolderFileDropId(null);
    setDropActive(false);
    await uploadFromDataTransfer(e.dataTransfer, folderId);
  };

  const onDragEnd = (event) => {
    setDraggingItem(null);
    const { active, over } = event;
    if (!over || readOnly || !writable || searchActive) return;
    const dragged = active.data.current?.item;
    if (!dragged) return;
    const overId = String(over.id);
    if (!overId.startsWith('folder-drop:')) return;
    const targetFolderId = overId.replace('folder-drop:', '') || null;
    if (dragged.type === 'folder' && dragged.itemId === targetFolderId) return;

    const sourceParentId = dragged.parentId ?? null;
    const destParentId = targetFolderId || null;
    if (sourceParentId === destParentId) {
      setDragActionModal({
        item: dragged,
        targetFolderId: destParentId,
        destinationName: displayItems.find((i) => i.itemId === targetFolderId)?.name || 'this folder',
        sameFolder: true,
      });
      return;
    }

    const destFolder = displayItems.find((i) => i.itemId === targetFolderId);
    setDragActionModal({
      item: dragged,
      targetFolderId: destParentId,
      destinationName: destFolder?.name || STORAGE_BOX_ROOT_LABEL,
      sameFolder: false,
    });
  };

  const closeDragActionModal = () => {
    if (dragActionBusy) return;
    setDragActionModal(null);
  };

  const finishDragAction = async (mode) => {
    if (!dragActionModal || dragActionBusy) return;
    const { item, targetFolderId } = dragActionModal;
    setDragActionBusy(true);
    try {
      if (mode === 'move') {
        await storageBoxApi.moveItem(item.itemId, targetFolderId);
        if (selected?.itemId === item.itemId) setSelected(null);
        notify('Moved');
      } else {
        await storageBoxApi.copyItem(item.itemId, targetFolderId);
        notify('Copied');
      }
      refreshTree();
      await reloadCurrentView();
      setDragActionModal(null);
    } catch (e) {
      notify(e.message || (mode === 'move' ? 'Move failed' : 'Copy failed'));
    } finally {
      setDragActionBusy(false);
    }
  };

  const breadcrumbTrail = useMemo(() => {
    if (inSharedLockView) {
      return buildSharedLockBreadcrumbTrail(activeSharedShare, breadcrumbs, lockRootItemId);
    }
    if (inLock) {
      if (!breadcrumbs.length) {
        return lockRootItemId
          ? [{ id: lockRootItemId, name: STORAGE_BOX_LOCK_LABEL }]
          : [{ id: null, name: STORAGE_BOX_LOCK_LABEL }];
      }
      return breadcrumbs.map((b) => ({ id: b.itemId, name: b.name }));
    }
    return [
      { id: null, name: STORAGE_BOX_ROOT_LABEL },
      ...breadcrumbs.map((b) => ({ id: b.itemId, name: b.name })),
    ];
  }, [breadcrumbs, inLock, inSharedLockView, activeSharedShare, lockRootItemId]);

  const homeTitle = storageBoxHomeTitle(APP_NAME);

  const pageTitle = useMemo(() => {
    if (readOnly) return 'Shared with you';
    if (searchActive) return homeTitle;
    if (inBin) return STORAGE_BOX_BIN_LABEL;
    if (inSharedLockView) {
      return sharedLockPageTitle(activeSharedShare, breadcrumbs, lockRootItemId);
    }
    if (inLock) return STORAGE_BOX_LOCK_LABEL;
    if (inFavorites) return STORAGE_BOX_FAVORITES_LABEL;
    if (inLabel && activeLabel) return activeLabel;
    if (inColorTag && activeColorTag) return `Tag: ${colorTagDisplayName(activeColorTag)}`;
    if (!breadcrumbs.length) return homeTitle;
    return breadcrumbs.map((b) => b.name).join(' / ');
  }, [
    readOnly,
    searchActive,
    breadcrumbs,
    homeTitle,
    inBin,
    inLock,
    inSharedLockView,
    activeSharedShare,
    lockRootItemId,
    inFavorites,
    inLabel,
    inColorTag,
    activeLabel,
    activeColorTag,
  ]);

  useEffect(() => {
    const tabTitle = readOnly ? 'Shared with you' : pageTitle === homeTitle ? homeTitle : `${pageTitle} · ${homeTitle}`;
    document.title = tabTitle;
    return () => {
      document.title = APP_NAME;
    };
  }, [pageTitle, homeTitle, readOnly]);

  const goToCrumb = (id) => {
    clearSearch();
    if (inSharedLockView) {
      if (isSharedLockOwnerCrumbId(id)) {
        if (activeSharedShare?.itemId) navigateTo(activeSharedShare.itemId);
        return;
      }
      if (id != null) navigateTo(id);
      return;
    }
    if (inLock) {
      if (id == null || (lockRootItemId && id === lockRootItemId)) {
        navigateTo(lockRootItemId || null);
      } else {
        navigateTo(id);
      }
      return;
    }
    if (id == null) goToRoot();
    else navigateTo(id);
  };

  const closeEmptyBinModal = () => {
    if (emptyBinBusy) return;
    setEmptyBinModalOpen(false);
  };

  const confirmEmptyBin = async () => {
    if (emptyBinBusy) return;
    setEmptyBinBusy(true);
    try {
      await emptyBin();
      setEmptyBinModalOpen(false);
    } finally {
      setEmptyBinBusy(false);
    }
  };

  return (
    <div className="storagebox-page sb-drive-page">
      <StorageBoxToast notice={notice} />

      <CenterModalWindow
        isOpen={mobilePreviewOpen}
        onClose={() => setMobilePreviewOpen(false)}
        width="min(480px, calc(100vw - 24px))"
        height="min(85dvh, 640px)"
        contentClassName="sb-drive-mobile-preview-modal"
        contentStyle={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <PreviewPanel
          className="sb-drive-workspace-preview sb-drive-mobile-preview"
          item={selected}
          publicToken={publicToken}
          readOnly={readOnly || !writable}
          onShare={openShareModal}
          onOpenMedia={openMediaViewer}
          onOpenAudio={openAudioPlayer}
          onQueueAudioPlayNext={queueAudioPlayNext}
          onOpenTextEditor={openTextEditor}
        />
      </CenterModalWindow>

      <StorageBoxTextEditorModal
        isOpen={textEditorOpen}
        onClose={() => {
          textEditorDismissedRef.current = true;
          setTextEditorOpen(false);
        }}
        item={selected}
      />

      <MobileMediaViewerOverlay
        isOpen={mediaViewerOpen && mediaViewerItems.length > 0}
        onClose={() => {
          mediaViewerDismissedRef.current = true;
          setMediaViewerOpen(false);
          flushLockViewerDecryptedMedia();
        }}
        items={mediaViewerItems}
        index={mediaViewerIndex}
        onIndexChange={setMediaViewerIndex}
        getLockMasterKeyBytes={inLockVault ? getLockMasterKeyBytes : undefined}
        lockVaultReady={!inLockVault || lockMasterKeyReady}
        onLockVideoError={inLockVault ? handleLockVideoError : undefined}
      />

      <StorageBoxMoveCopyModal
        isOpen={Boolean(dragActionModal)}
        onClose={closeDragActionModal}
        itemName={dragActionModal?.item?.name || ''}
        destinationName={dragActionModal?.destinationName}
        sameFolder={Boolean(dragActionModal?.sameFolder)}
        busy={dragActionBusy}
        onMove={() => finishDragAction('move')}
        onCopy={() => finishDragAction('copy')}
      />

      <ConfirmDangerModal
        isOpen={emptyBinModalOpen}
        onClose={closeEmptyBinModal}
        busy={emptyBinBusy}
        onConfirm={confirmEmptyBin}
        title="Empty Bin?"
        description="Permanently delete everything in the Bin? This cannot be undone."
        confirmLabel="Empty Bin"
        confirmBusyLabel="Emptying…"
      />

      <ConfirmDangerModal
        isOpen={bulkDeleteOpen}
        onClose={() => {
          if (!bulkActionBusy) setBulkDeleteOpen(false);
        }}
        busy={bulkActionBusy}
        onConfirm={runBulkDelete}
        title={
          bulkSelectedItems.length === 1
            ? 'Delete item?'
            : `Delete ${bulkSelectedItems.length} items?`
        }
        description="Selected items will move to the Bin for 30 days. You can restore them before they are permanently removed."
        confirmLabel="Move to Bin"
        confirmBusyLabel="Deleting…"
      />

      <StorageBoxDestinationPickerModal
        isOpen={Boolean(bulkDestModal)}
        mode={bulkDestModal?.mode || 'move'}
        busy={bulkActionBusy}
        rootId={destinationRootId}
        rootLabel={destinationRootLabel}
        loadFolders={loadDestinationFolders}
        selectedFolderIds={bulkSelectedFolderIds}
        itemCount={bulkSelectedItems.length}
        onClose={() => {
          if (!bulkActionBusy) setBulkDestModal(null);
        }}
        onConfirm={(folderId) => runBulkTransfer(bulkDestModal?.mode || 'move', folderId)}
      />

      <div className="sb-drive-workspace-grid">
      <div className="sb-drive-topbar">
        {!readOnly && writable && !inSpecialView ? <StorageBoxNewMenu /> : null}
        {inBin ? (
          <button
            type="button"
            className="sb-danger-btn sb-danger-btn--compact sb-empty-bin-btn"
            onClick={() => setEmptyBinModalOpen(true)}
            aria-label="Empty Bin"
            title="Empty Bin"
          >
            <FiTrash2 className="sb-empty-bin-btn__icon" aria-hidden />
            <span className="sb-empty-bin-btn__label">Empty Bin</span>
          </button>
        ) : null}
        {!readOnly ? <StorageBoxSearchField className="sb-drive-search-wrap--toolbar" /> : null}

        <StorageBoxSortSelect />

        <div className="sb-drive-view-toggle" role="group" aria-label="View mode">
          {bulkSelectEnabled && !bulkSelectMode ? (
            <button
              type="button"
              className="sb-drive-view-btn"
              onClick={() => setBulkSelectMode(true)}
              aria-label="Select items"
              title="Select items"
            >
              <FiCheckSquare aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            className={viewMode === 'list' ? 'sb-drive-view-btn sb-drive-view-btn--active' : 'sb-drive-view-btn'}
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            title="List view"
          >
            <FiList />
          </button>
          <button
            type="button"
            className={viewMode === 'grid' ? 'sb-drive-view-btn sb-drive-view-btn--active' : 'sb-drive-view-btn'}
            onClick={() => setViewMode('grid')}
            aria-pressed={viewMode === 'grid'}
            title="Grid view"
          >
            <FiGrid />
          </button>
        </div>

        <button
          type="button"
          className="sb-drive-view-btn sb-drive-info-btn"
          onClick={() => setMobilePreviewOpen(true)}
          aria-label="Details"
          title="Details"
        >
          <FiInfo />
        </button>
      </div>

      {inBin ? (
        <p className="sb-drive-search-hint">
          {vaultScope === 'lock' ? 'Lock folder bin only. ' : `Main ${STORAGE_BOX_ROOT_LABEL} bin only. `}
          Items remain for {STORAGE_BOX_BIN_RETENTION_DAYS} days, then are deleted permanently. Use &quot;Empty Bin&quot;
          to remove everything now.
        </p>
      ) : inFavorites ? (
        <p className="sb-drive-search-hint">
          {vaultScope === 'lock'
            ? 'Favourites inside your Lock folder only.'
            : `Files and folders you marked as favorites in main ${STORAGE_BOX_ROOT_LABEL}.`}
        </p>
      ) : inLabel ? (
        <p className="sb-drive-search-hint">
          Items with the label &quot;{activeLabel}&quot;. Change labels from the details panel.
        </p>
      ) : inColorTag ? (
        <p className="sb-drive-search-hint">
          Items with the {colorTagDisplayName(activeColorTag)} tag. Set tags from the details panel.
        </p>
      ) : !searchActive ? (
        <div className="sb-drive-breadcrumbs">
          <h1 className="sb-drive-title sb-drive-title--sr" title={pageTitle}>{pageTitle}</h1>
          <div className="sb-nav-btns sb-drive-nav-btns">
            <button type="button" onClick={goBack} disabled={navIndex <= 0} aria-label="Back">
              <FiArrowLeft />
            </button>
            <button type="button" onClick={goForward} disabled={navIndex >= navStackLength - 1} aria-label="Forward">
              <FiArrowRight />
            </button>
          </div>
          <StorageBoxBreadcrumbTrail trail={breadcrumbTrail} onCrumb={goToCrumb} />
        </div>
      ) : (
        <p className="sb-drive-search-hint">
          {searchLoading ? (
            <span className="sb-drive-search-hint-skeleton" aria-busy="true" aria-label="Searching" />
          ) : vaultScope === 'lock' ? (
            `Results in Lock folder for “${searchQuery.trim()}”`
          ) : (
            `Results in ${STORAGE_BOX_ROOT_LABEL} for “${searchQuery.trim()}”`
          )}
        </p>
      )}

      {bulkSelectEnabled && bulkSelectMode ? (
        <div className="sb-drive-selection-row">
          <StorageBoxBulkSelectBar
            checkedCount={bulkSelectedItems.length}
            totalSelectable={bulkSelectableList.length}
            busy={bulkActionBusy}
            onExitSelectMode={exitBulkSelectMode}
            onSelectAll={selectAllBulk}
            onSelectNone={selectNoneBulk}
            onDelete={() => setBulkDeleteOpen(true)}
            onMoveTo={() => setBulkDestModal({ mode: 'move' })}
            onCopyTo={() => setBulkDestModal({ mode: 'copy' })}
          />
        </div>
      ) : null}

      <div className="sb-drive-workspace-primary">
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setDraggingItem(e.active.data.current?.item)}
          onDragEnd={onDragEnd}
        >
          <section
            className={`sb-main sb-drive-main ${viewMode === 'list' ? 'sb-drive-main--list' : ''} ${dropActive ? 'sb-main--drop-active' : ''}`}
            onDragOver={(e) => {
              if (searchActive || readOnly || !writable || inSpecialView) return;
              if (!allowExternalFileDrop(e)) return;
              setFolderFileDropId(null);
              setDropActive(true);
            }}
            onDragLeave={(e) => {
              const related = e.relatedTarget;
              if (related && e.currentTarget.contains(related)) return;
              setDropActive(false);
            }}
            onDrop={onDrop}
          >
            {loadError && !isLoading ? (
              <div className="sb-drive-error">
                <button type="button" className="sb-primary-btn sb-drive-retry" onClick={retryLoad}>
                  Refresh
                </button>
              </div>
            ) : isLoading ? (
              <StorageBoxWorkspaceSkeleton
                variant={viewMode === 'list' ? 'list' : 'grid'}
              />
            ) : !displayItems.length ? (
              <p className="sb-drive-empty">
                {searchActive
                  ? 'No files or folders match your search.'
                  : inBin
                    ? 'The Bin is empty.'
                    : inFavorites
                      ? 'No favorites yet — star files or folders from the details panel.'
                      : inLabel
                        ? 'No items use this label.'
                        : inColorTag
                          ? 'No items use this tag colour.'
                          : 'This folder is empty — drop files here or use New.'}
              </p>
            ) : viewMode === 'grid' && !searchActive ? (
              <StorageBoxItemGrid
                items={displayItems}
                inFavoritesView={inFavorites}
                selectedId={selected?.itemId}
                publicToken={publicToken}
                lockSessionToken={inLockVault ? lockSessionToken : null}
                readOnly={readOnly || !writable}
                folderFileDropId={folderFileDropId}
                onFolderFileDragOver={onFolderFileDragOver}
                onFolderFileDragLeave={onFolderFileDragLeave}
                onFolderFileDrop={onFolderFileDrop}
                onSelectItem={selectItem}
                onOpenItem={handleOpenFile}
                onShare={openShareModal}
                onDelete={openDeleteModal}
                onRename={openRenameModal}
                onQueueAudioPlayNext={queueAudioPlayNext}
                dndDisabled={readOnly || !writable || searchActive || inSpecialView || bulkSelectMode}
                bulkSelectMode={bulkSelectMode}
                bulkCheckedIds={bulkCheckedIds}
                onToggleBulkCheck={toggleBulkCheck}
                bulkSelectOptions={bulkSelectOptions}
              />
            ) : (
              <div className="sb-table-wrap">
                <table
                  className={`sb-table sb-drive-table${searchActive ? ' sb-drive-table--search' : ''}`}
                >
                  <thead>
                    <tr>
                      {bulkSelectMode ? (
                        <th className="sb-drive-col-bulk" scope="col" aria-label="Select">
                          <span className="sb-drive-col-bulk__inner">
                            <input
                              type="checkbox"
                              className="sb-bulk-check sb-bulk-check--list"
                              checked={
                                bulkSelectableList.length > 0 &&
                                bulkSelectableList.every((row) => bulkCheckedIds.has(row.itemId))
                              }
                              onChange={(e) => {
                                if (e.target.checked) selectAllBulk();
                                else selectNoneBulk();
                              }}
                              aria-label="Select all"
                            />
                          </span>
                        </th>
                      ) : null}
                      <th className="sb-drive-col-name">Name</th>
                      {searchActive || inBin || inLabel || inColorTag ? (
                        <th className="sb-drive-col-location">Location</th>
                      ) : null}
                      {inBin ? <th className="sb-drive-col-purge">Days left</th> : null}
                      <th className="sb-drive-col-meta" aria-label="Properties" />
                      <th className="sb-drive-col-date">Last modified</th>
                      <th className="sb-drive-col-size">Size</th>
                      <th className="sb-drive-col-share" aria-label="Share" />
                      {!readOnly && writable && !bulkSelectMode ? (
                        <th className="sb-drive-col-actions" aria-label="Actions" />
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {displayItems.map((item) => {
                      const RowWrapper =
                        !searchActive && !inSpecialView && item.type === 'folder' ? FolderRow : DraggableRow;
                      const isSelected = selected?.itemId === item.itemId;
                      const canBulkSelect = isStorageBoxBulkSelectable(item, bulkSelectOptions);
                      const isBulkChecked = bulkCheckedIds.has(item.itemId);
                      const rowProps =
                        !searchActive && !inSpecialView && item.type === 'folder'
                          ? {
                              item,
                              disabled: readOnly || !writable || inSpecialView || bulkSelectMode,
                              isSelected,
                              fileDropActive: folderFileDropId === item.itemId,
                              onFolderFileDragOver,
                              onFolderFileDragLeave,
                              onFolderFileDrop,
                            }
                          : {
                              item,
                              disabled: readOnly || !writable || searchActive || inSpecialView || bulkSelectMode,
                              isSelected,
                            };
                      return (
                        <RowWrapper key={item.itemId} {...rowProps}>
                          {bulkSelectMode ? (
                            <td className="sb-drive-col-bulk">
                              <span className="sb-drive-col-bulk__inner">
                                {canBulkSelect ? (
                                  <input
                                    type="checkbox"
                                    className="sb-bulk-check sb-bulk-check--list"
                                    checked={isBulkChecked}
                                    onChange={() => toggleBulkCheck(item.itemId)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select ${item.name}`}
                                  />
                                ) : null}
                              </span>
                            </td>
                          ) : null}
                          <td className="sb-drive-name-cell">
                            <div className="sb-drive-name-block">
                              <button
                                type="button"
                                className="sb-name-btn sb-drive-name-btn"
                                onClick={() => {
                                  if (bulkSelectMode && canBulkSelect) {
                                    toggleBulkCheck(item.itemId);
                                    return;
                                  }
                                  if (item.type === 'folder') handleOpenFile(item);
                                  else selectItem(item);
                                }}
                                onDoubleClick={() => {
                                  if (bulkSelectMode) return;
                                  if (item.type === 'file') handleOpenFile(item);
                                }}
                              >
                                <span className="sb-drive-name-thumb-wrap">
                                  <DriveItemThumbnail
                                    item={item}
                                    publicToken={publicToken}
                                    lockSessionToken={inLockVault ? lockSessionToken : null}
                                    variant="list"
                                  />
                                </span>
                              <span className="sb-drive-name-title" title={item.name}>{item.name}</span>
                            </button>
                            </div>
                          </td>
                          {searchActive || inBin || inLabel || inColorTag ? (
                            <td className="sb-drive-location sb-drive-col-location">
                              {item.locationPath || STORAGE_BOX_ROOT_LABEL}
                            </td>
                          ) : null}
                          {inBin ? (
                            <td className="sb-drive-col-purge">
                              {item.daysUntilPurge != null ? item.daysUntilPurge : '—'}
                            </td>
                          ) : null}
                          <td className="sb-drive-col-meta">
                            <StorageBoxItemMetaBadge
                              item={item}
                              inFavoritesView={inFavorites}
                              variant="list"
                            />
                          </td>
                          <td className="sb-drive-col-date" title={item.updatedAt || item.createdAt || ''}>
                            {item.updatedAt
                              ? new Date(item.updatedAt).toLocaleDateString()
                              : item.createdAt
                                ? new Date(item.createdAt).toLocaleDateString()
                                : '—'}
                          </td>
                          <td className="sb-drive-col-size" title={item.type === 'file' ? formatBytes(item.sizeBytes) : ''}>
                            {item.type === 'file' ? formatBytes(item.sizeBytes) : '—'}
                          </td>
                          <td className="sb-drive-col-share">
                            {item.isShared ? <FiShare2 className="sb-shared-icon" title="Shared" /> : null}
                          </td>
                          {!readOnly && writable && !bulkSelectMode ? (
                            <td className="sb-row-actions sb-drive-col-actions">
                              <StorageBoxItemActionsMenu
                                item={item}
                                onShare={openShareModal}
                                onRename={openRenameModal}
                                onDelete={openDeleteModal}
                                onQueueAudioPlayNext={queueAudioPlayNext}
                              />
                            </td>
                          ) : null}
                        </RowWrapper>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!readOnly && writable && !searchActive && !inSpecialView ? (
              <p className="sb-drop-hint">Drag files or folders here to upload</p>
            ) : null}
          </section>

          <DragOverlay>
            {draggingItem ? <div className="sb-drag-ghost">{draggingItem.name}</div> : null}
          </DragOverlay>
        </DndContext>
      </div>

        <div className="sb-drive-workspace-preview-slot sb-drive-workspace-preview-slot--desktop">
          <PreviewPanel
            className="sb-drive-workspace-preview"
            item={selected}
            publicToken={publicToken}
            readOnly={readOnly || !writable}
            onShare={openShareModal}
            onOpenMedia={openMediaViewer}
            onOpenAudio={openAudioPlayer}
          onQueueAudioPlayNext={queueAudioPlayNext}
            onOpenTextEditor={openTextEditor}
          />
        </div>
      </div>
    </div>
  );
}
